import is from '@sindresorhus/is';
import { z } from 'zod';

import { type LanguageCodeForJSA, languageCodeSchemaForJSA } from '~/shared/internationalization';

import type { ProfileTaskParentContext } from '../model/profile-task';
import {
    isWholeOrNoProfileAddress,
    optionalProfileAddressShape,
    profileAddressMessage,
    segmentIdSchema,
    toProfileAddress,
} from './automation-tile-address';

export const JSA_AUTOMATION_STEP_TILE_TYPE = 'v1.jsa.automation-step' as const;
export const AUTOMATION_STEP_TILE_SLOT_KEY = 'jsa.automation-step';
export const AUTOMATION_STEP_TILE_WIDTH = '500px';

/** One workflow step of a task's stored assessment; addresses the task like the task tile does. */
export const automationStepTileSchema = z
    .object({
        ...optionalProfileAddressShape,
        language: languageCodeSchemaForJSA.optional(),
        taskId: segmentIdSchema,
        taskVersionId: z.string().trim().min(1).optional(),
        type: z.literal(JSA_AUTOMATION_STEP_TILE_TYPE),
        workflowStepId: segmentIdSchema,
    })
    .refine(isWholeOrNoProfileAddress, { message: profileAddressMessage });

export type AutomationStepTilePayload = z.infer<typeof automationStepTileSchema>;

export const isAutomationStepTilePayload = (payload: unknown): payload is AutomationStepTilePayload =>
    is.plainObject(payload) && payload['type'] === JSA_AUTOMATION_STEP_TILE_TYPE;

export const createAutomationStepTilePayload = ({
    context,
    language,
    taskId,
    taskVersionId,
    workflowStepId,
}: {
    /** The job profile the task is listed on; absent for a work task opened on its own. */
    readonly context?: ProfileTaskParentContext;
    readonly language?: LanguageCodeForJSA;
    readonly taskId: string;
    readonly taskVersionId?: string;
    readonly workflowStepId: string;
}): AutomationStepTilePayload => ({
    ...toProfileAddress(context),
    ...(is.undefined(language) ? {} : { language }),
    taskId,
    ...(is.undefined(taskVersionId) ? {} : { taskVersionId }),
    type: JSA_AUTOMATION_STEP_TILE_TYPE,
    workflowStepId,
});
